import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { onboardingApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Loader2, ArrowRight, ArrowLeft, Check, Sparkles, Mail } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import VoicePicker from '../components/VoicePicker';
import { loadTTSVoices, getSavedTTSVoice, saveTTSVoice } from '../lib/tts';

// Steps in order. Each step is a single decision so the flow feels one-thing-at-a-time.
const STEP_LEVEL_GOAL   = 0;
const STEP_INTERESTS    = 1;
const STEP_STYLE_GENDER = 2;
const STEP_PHONETICS    = 3;
// Paid plans only: Free has the browser voice and nothing to choose.
const STEP_VOICE        = 4;
const STEP_TARGETS      = 5;
const STEP_DONE         = 6;

const VOICE_PREVIEW_TEXT = 'Bonjour, voici un aperçu de cette voix.';

const LEVELS = [
    { id: 'beginner',     label: 'Beginner',     hint: 'Just starting or brushing up basics' },
    { id: 'intermediate', label: 'Intermediate', hint: 'Can hold a simple conversation' },
    { id: 'advanced',     label: 'Advanced',     hint: 'Comfortable, but want to sound more natural' },
];

const GOALS = [
    { id: 'living_in_france', label: 'Living in France' },
    { id: 'travel',           label: 'Travel to France' },
    { id: 'work',             label: 'Work reasons' },
    { id: 'family',           label: 'Family reasons' },
    { id: 'fun',              label: 'Just for fun' },
];

const SESSION_LENGTHS = [5, 10, 15, 20, 30];
const WEEKLY_TARGETS  = [1, 3, 5, 7];

// Small reusable pill/tile so the whole flow reads as one thing visually.
const Tile = ({ selected, onClick, children, className = 'px-4' }) => (
    <button
        type="button"
        onClick={onClick}
        className={`w-full text-left rounded-xl border transition-all py-3 ${className} ${
            selected
                ? 'border-primary bg-primary/5 shadow-sm'
                : 'border-stone-200 bg-white hover:border-stone-300'
        }`}
    >
        {children}
    </button>
);

const StepHeader = ({ n, total, title, subtitle }) => (
    <div className="mb-6">
        <p className="text-xs font-medium text-stone-400 uppercase tracking-wide mb-2">
            Step {n} of {total}
        </p>
        <h2 className="text-xl md:text-2xl font-semibold text-stone-800 mb-1">{title}</h2>
        {subtitle && <p className="text-sm text-stone-500">{subtitle}</p>}
    </div>
);

const ChangeableNote = () => (
    <p className="mt-3 text-xs text-stone-400 italic">
        This is just your starting default. You can change it anytime in Settings.
    </p>
);

const Onboarding = () => {
    const navigate = useNavigate();
    const { user, refreshUser } = useAuth();
    // ?redo=1 from the dashboard prompt, for accounts that never had the
    // wizard. Fixed at mount, so finishing (which ends their eligibility)
    // does not bounce them off the done screen.
    const [searchParams] = useSearchParams();
    const [redo] = useState(() => searchParams.get('redo') === '1' && !!user?.can_redo_onboarding);

    const [step, setStep] = useState(STEP_LEVEL_GOAL);
    const [submitting, setSubmitting] = useState(false);
    const [topicsLoading, setTopicsLoading] = useState(true);
    const [topics, setTopics] = useState([]);
    // Overwritten by the backend on load. 3 is the Free limit, the safe floor
    // to start from if the request is slow.
    const [maxTopics, setMaxTopics] = useState(3);
    const [seededCount, setSeededCount] = useState(0);

    // Selections
    const [frenchLevel, setFrenchLevel] = useState('beginner');
    const [goal, setGoal] = useState('');
    const [interestTopics, setInterestTopics] = useState([]);
    const [formality, setFormality] = useState('informal');
    const [gender, setGender] = useState('female');
    const [sessionLength, setSessionLength] = useState(10);
    const [weeklyTarget, setWeeklyTarget] = useState(5);
    const [phoneticStyle, setPhoneticStyle] = useState('english_syllables');
    // On by default, matching the account, which already has reminders set from
    // the moment it was created. Turning the switch off here clears them.
    const [reminderEnabled, setReminderEnabled] = useState(true);
    const [reminderTime, setReminderTime] = useState('19:00');
    // Natural voices, for the voice step. /tts/voices returns none on Free,
    // so the step only appears when there is something to choose.
    const [ttsVoices, setTtsVoices] = useState([]);
    const [voice, setVoice] = useState(getSavedTTSVoice);
    const showVoice = (user?.plan || 'free') !== 'free' && ttsVoices.length > 0;

    useEffect(() => {
        if ((user?.plan || 'free') === 'free') return;
        loadTTSVoices().then(setTtsVoices).catch(() => { /* no voice step */ });
    }, [user?.plan]);

    // Load the topic list from the backend (single source of truth). The pick
    // limit comes with it and depends on their plan, so it is never hardcoded
    // here: Free picks 3, paid picks 5.
    useEffect(() => {
        onboardingApi.getTopics()
            .then(res => {
                setTopics(res.data.topics || []);
                // 0 is real on a rerun with no phrase space left.
                if (res.data.max_topics != null) setMaxTopics(res.data.max_topics);
            })
            .catch(() => toast.error('Could not load topics. Reload and try again.'))
            .finally(() => setTopicsLoading(false));
    }, []);

    // If they already onboarded, don't let them re-run the flow (unless it is
    // the offered rerun) — send them straight to the app.
    useEffect(() => {
        if (user?.onboarding_complete && !redo) navigate('/dashboard', { replace: true });
    }, [user, navigate, redo]);

    const totalSteps = showVoice ? 6 : 5;
    // The step after or before this one, skipping the voice step on Free.
    const nextStep = (s) => (s + 1 === STEP_VOICE && !showVoice ? s + 2 : s + 1);
    const prevStep = (s) => Math.max(0, s - 1 === STEP_VOICE && !showVoice ? s - 2 : s - 1);

    const toggleInterest = (id) => {
        setInterestTopics(prev => {
            if (prev.includes(id)) return prev.filter(x => x !== id);
            if (prev.length >= maxTopics) return prev; // keeps setup light, and matches their plan
            return [...prev, id];
        });
    };

    // Two groups rather than one flat list. Ten topics in a single column is
    // the wall of choice this app exists to avoid, and situational topics and
    // speaking drills are different kinds of decision: what to say in a place
    // versus how to make a sound.
    const topicGroups = useMemo(() => ([
        {
            key: 'situational',
            title: 'Situational topics',
            hint: 'What to say, where you actually are',
            items: topics.filter(t => t.kind === 'situational'),
        },
        {
            key: 'drill',
            title: 'Speaking drills',
            hint: 'The sounds English speakers find hardest',
            items: topics.filter(t => t.kind === 'drill'),
        },
        {
            key: 'chunk',
            title: 'Conversation chunks',
            hint: 'Little openers that fit into any conversation',
            items: topics.filter(t => t.kind === 'chunk'),
        },
    ].filter(g => g.items.length > 0)), [topics]);

    const canAdvance = useMemo(() => {
        if (step === STEP_LEVEL_GOAL)   return !!frenchLevel && !!goal;
        if (step === STEP_INTERESTS)    return true; // 0 topics allowed — user can skip
        if (step === STEP_STYLE_GENDER) return !!formality && !!gender;
        if (step === STEP_TARGETS)      return !!sessionLength && !!weeklyTarget;
        return true;
    }, [step, frenchLevel, goal, formality, gender, sessionLength, weeklyTarget]);

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const res = await onboardingApi.complete({
                french_level: frenchLevel,
                goal,
                interest_topics: interestTopics,
                formality_preference: formality,
                speaker_gender: gender,
                session_length_target: sessionLength,
                weekly_target: weeklyTarget,
                phonetic_style: phoneticStyle,
                reminder_enabled: reminderEnabled,
                reminder_time: reminderEnabled ? reminderTime : null,
            });
            setSeededCount(res.data.seeded_collections || 0);
            // Refresh the /auth/me response so the route gate sees onboarding_complete=true.
            if (refreshUser) await refreshUser();
            // Clear any prior "dismissed" flag so this user reliably sees the
            // Dashboard starter checklist after completing onboarding, even
            // if they went through the flow before (e.g. a re-test).
            if (user?.id) {
                try { localStorage.removeItem(`onboarding_checklist_dismissed_${user.id}`); }
                catch { /* localStorage disabled — safe to ignore */ }
            }
            setStep(STEP_DONE);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Something went wrong. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-fixed bg-gradient-to-b from-foreground to-primary text-primary-foreground">
            <header className="sticky top-0 z-40 bg-foreground/90 backdrop-blur-md border-b border-primary-foreground/10">
                <div className="max-w-2xl mx-auto px-4 md:px-6 py-4">
                    <h1 className="text-xl font-bold text-primary-foreground">Welcome to Mimique 💚</h1>
                    <p className="text-sm text-primary-foreground/70">
                        Mimique is about practising spoken French and mastering pronunciation. Let's set you up and ready to go!
                    </p>
                </div>
            </header>

            <main className="max-w-2xl mx-auto px-4 md:px-6 py-6 md:py-10">
                <Card className="bg-white border-stone-100 shadow-sm">
                    <CardContent className="p-5 md:p-8">
                        {step === STEP_LEVEL_GOAL && (
                            <>
                                <StepHeader
                                    n={1} total={totalSteps}
                                    title="Your French level and reason for learning"
                                    subtitle="So Mimique can pick the right phrases for you."
                                />
                                <div className="space-y-6">
                                    <div>
                                        <p className="text-sm font-medium text-stone-700">Your French level</p>
                                        <p className="text-xs text-stone-400 italic mb-3">This is just your starting default. You can change it anytime in Settings.</p>
                                        <div className="space-y-2">
                                            {LEVELS.map(l => (
                                                <Tile key={l.id} selected={frenchLevel === l.id} onClick={() => setFrenchLevel(l.id)}>
                                                    <div className="flex items-center justify-between gap-3">
                                                        <div>
                                                            <p className="text-sm font-medium text-stone-800">{l.label}</p>
                                                            <p className="text-xs text-stone-500">{l.hint}</p>
                                                        </div>
                                                        {frenchLevel === l.id && <Check className="h-4 w-4 text-primary flex-shrink-0" />}
                                                    </div>
                                                </Tile>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-stone-700 mb-3">Why are you learning?</p>
                                        <div className="grid grid-cols-2 gap-2">
                                            {GOALS.map(g => (
                                                <Tile key={g.id} selected={goal === g.id} onClick={() => setGoal(g.id)}>
                                                    <div className="flex items-center justify-between gap-2">
                                                        <span className="text-sm text-stone-700">{g.label}</span>
                                                        {goal === g.id && <Check className="h-4 w-4 text-primary flex-shrink-0" />}
                                                    </div>
                                                </Tile>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {step === STEP_INTERESTS && (
                            <>
                                <StepHeader
                                    n={2} total={totalSteps}
                                    title={`Pick up to ${maxTopics} subjects to start with`}
                                    subtitle="Each becomes a small starter collection (about 10 phrases). You can add more anytime."
                                />
                                {topicsLoading ? (
                                    <div className="flex items-center justify-center py-8">
                                        <Loader2 className="h-5 w-5 animate-spin text-stone-400" />
                                    </div>
                                ) : (
                                    <>
                                        <div className="space-y-5">
                                            {topicGroups.map(group => (
                                                <div key={group.key} className="space-y-2">
                                                    <div>
                                                        <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">{group.title}</p>
                                                        <p className="text-xs text-stone-400">{group.hint}</p>
                                                    </div>
                                                    {group.items.map(t => {
                                                        const selected = interestTopics.includes(t.id);
                                                        const capReached = !selected && interestTopics.length >= maxTopics;
                                                        return (
                                                            <Tile
                                                                key={t.id}
                                                                selected={selected}
                                                                onClick={capReached ? () => {} : () => toggleInterest(t.id)}
                                                            >
                                                                <div className={`flex items-center gap-3 ${capReached ? 'opacity-40' : ''}`}>
                                                                    {/* Display only. The row is the button: with its own
                                                                        handler, a tap on the box toggled twice (box, then
                                                                        the row) and looked like it did nothing. */}
                                                                    <Checkbox
                                                                        checked={selected}
                                                                        disabled={capReached}
                                                                        tabIndex={-1}
                                                                        aria-hidden="true"
                                                                        className="pointer-events-none"
                                                                    />
                                                                    <p className="text-sm font-medium text-stone-800">{t.label}</p>
                                                                </div>
                                                            </Tile>
                                                        );
                                                    })}
                                                </div>
                                            ))}
                                        </div>
                                        <p className="mt-3 text-xs text-stone-500 text-right">
                                            {interestTopics.length}/{maxTopics} chosen
                                        </p>
                                    </>
                                )}
                                <p className="mt-4 text-xs text-stone-500 flex items-start gap-2">
                                    <Sparkles className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-amber-500" />
                                    <span>
                                        These are just starter collections to get you going. You can edit any phrase, delete what you don't want, add your own phrases anytime, or tap <strong>Generate AI</strong> to make collections of phrases that fit exactly what you want to practise, no matter what the subject.
                                    </span>
                                </p>
                            </>
                        )}

                        {step === STEP_STYLE_GENDER && (
                            <>
                                <StepHeader
                                    n={3} total={totalSteps}
                                    title="Style and gender"
                                    subtitle="So phrases sound right for you and use the correct French agreement."
                                />
                                <div className="space-y-6">
                                    <div>
                                        <p className="text-sm font-medium text-stone-700 mb-3">Phrase style</p>
                                        <div className="grid grid-cols-2 gap-2">
                                            <Tile selected={formality === 'informal'} onClick={() => setFormality('informal')}>
                                                <p className="text-sm font-medium text-stone-800">Informal (tu)</p>
                                                <p className="text-xs text-stone-500">Friends, family, everyday</p>
                                            </Tile>
                                            <Tile selected={formality === 'formal'} onClick={() => setFormality('formal')}>
                                                <p className="text-sm font-medium text-stone-800">Formal (vous)</p>
                                                <p className="text-xs text-stone-500">Work, admin, politer register</p>
                                            </Tile>
                                        </div>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-stone-700 mb-3">Your gender</p>
                                        <p className="text-xs text-stone-500 mb-3">Used so French agreement is correct (e.g. "allé" vs "allée").</p>
                                        <div className="grid grid-cols-2 gap-2">
                                            <Tile selected={gender === 'female'} onClick={() => setGender('female')}>
                                                <p className="text-sm font-medium text-stone-800">Female</p>
                                            </Tile>
                                            <Tile selected={gender === 'male'} onClick={() => setGender('male')}>
                                                <p className="text-sm font-medium text-stone-800">Male</p>
                                            </Tile>
                                        </div>
                                    </div>
                                    <ChangeableNote />
                                </div>
                            </>
                        )}

                        {step === STEP_PHONETICS && (
                            <>
                                <StepHeader
                                    n={4} total={totalSteps}
                                    title="How do you want pronunciation written?"
                                    subtitle="Every phrase comes with a guide to how it sounds. Pick the style you find easiest to read."
                                />
                                <div className="space-y-3">
                                    <Tile selected={phoneticStyle === 'english_syllables'} onClick={() => setPhoneticStyle('english_syllables')}>
                                        <p className="text-sm font-medium text-stone-800">English-style syllables</p>
                                        <p className="text-xs text-stone-500 mb-2">Spelled the way an English speaker would read it. Capitals show the stressed syllable.</p>
                                        <p className="text-sm text-stone-800">zhuh voo-DRAY uhn kah-FAY</p>
                                    </Tile>
                                    <Tile selected={phoneticStyle === 'ipa'} onClick={() => setPhoneticStyle('ipa')}>
                                        <p className="text-sm font-medium text-stone-800">IPA (International Phonetic Alphabet)</p>
                                        <p className="text-xs text-stone-500 mb-2">The symbols used in dictionaries. Exact, but you need to know them.</p>
                                        <p className="text-sm text-stone-800">ʒə vu.dʁɛ œ̃ ka.fe</p>
                                    </Tile>
                                    <p className="text-xs text-stone-400">Both show "Je voudrais un café" (I'd like a coffee). If you're not sure, choose English-style syllables.</p>
                                    <p className="text-xs text-stone-400 italic">Choose carefully: this applies to all your phrases and is not easy to change later.</p>
                                </div>
                            </>
                        )}

                        {step === STEP_VOICE && (
                            <>
                                <StepHeader
                                    n={5} total={totalSteps}
                                    title="Choose your French voice"
                                    subtitle="The voice that reads your phrases. Tap play to hear each one."
                                />
                                <VoicePicker
                                    voices={ttsVoices}
                                    value={voice || ttsVoices[0]?.id}
                                    onChange={(v) => { setVoice(v); saveTTSVoice(v); }}
                                    speed={1}
                                    previewText={VOICE_PREVIEW_TEXT}
                                />
                                <ChangeableNote />
                            </>
                        )}

                        {step === STEP_TARGETS && (
                            <>
                                <StepHeader
                                    n={totalSteps} total={totalSteps}
                                    title="Set your practice goals"
                                    subtitle="Small and doable beats big and skipped. If streaks aren't your thing, ignore this."
                                />
                                <div className="space-y-6">
                                    <div>
                                        <p className="text-sm font-medium text-stone-700 mb-3">Daily practice length</p>
                                        {/* px-1 rather than the tile's px-4: five across on a
                                            phone left "min" running out of the box. */}
                                        <div className="grid grid-cols-5 gap-2">
                                            {SESSION_LENGTHS.map(n => (
                                                <Tile key={n} selected={sessionLength === n} onClick={() => setSessionLength(n)} className="px-1">
                                                    <p className="text-xs sm:text-sm font-medium text-stone-800 text-center whitespace-nowrap">{n} min</p>
                                                </Tile>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-stone-700 mb-3">Days per week</p>
                                        <div className="grid grid-cols-4 gap-2">
                                            {WEEKLY_TARGETS.map(n => (
                                                <Tile key={n} selected={weeklyTarget === n} onClick={() => setWeeklyTarget(n)} className="px-1">
                                                    <p className="text-sm font-medium text-stone-800 text-center whitespace-nowrap">{n} day{n === 1 ? '' : 's'}</p>
                                                </Tile>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="rounded-xl border border-stone-200 bg-stone-50/60 p-4">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="flex items-start gap-3">
                                                <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                                                    <Mail className="h-4 w-4" />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium text-stone-800">Daily email reminder</p>
                                                    <p className="text-xs text-stone-500">A short email if you haven't practised that day.</p>
                                                </div>
                                            </div>
                                            <Switch checked={reminderEnabled} onCheckedChange={setReminderEnabled} />
                                        </div>
                                    </div>
                                    <ChangeableNote />
                                </div>
                            </>
                        )}

                        {step === STEP_DONE && (
                            <div className="text-center py-4">
                                <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-4">
                                    <Check className="h-7 w-7" />
                                </div>
                                <h2 className="text-xl font-semibold text-stone-800 mb-2">You're all set</h2>
                                {seededCount > 0 ? (
                                    <p className="text-sm text-stone-600 max-w-md mx-auto">
                                        We've added <strong>{seededCount} starter collection{seededCount === 1 ? '' : 's'}</strong> to get you going.
                                        Every phrase is yours: edit anything, delete what you don't want, add your own phrases anytime, or tap <strong>Generate AI</strong> to make collections of phrases that fit exactly what you want to practise, no matter what the subject.
                                    </p>
                                ) : (
                                    <p className="text-sm text-stone-600 max-w-md mx-auto">
                                        Your settings are saved. Head to <strong>Phrases</strong> to add your own or tap <strong>Generate AI</strong> to make collections of phrases that fit exactly what you want to practise, no matter what the subject.
                                    </p>
                                )}
                                <Button className="mt-6" onClick={() => navigate('/dashboard', { replace: true })}>
                                    Go to my dashboard
                                    <ArrowRight className="h-4 w-4 ml-2" />
                                </Button>
                            </div>
                        )}

                        {/* Nav row (hidden on the done screen — it has its own CTA) */}
                        {step !== STEP_DONE && (
                            <div className="flex items-center justify-between gap-3 mt-8 pt-5 border-t border-stone-100">
                                <Button
                                    variant="ghost"
                                    onClick={() => setStep(prevStep)}
                                    disabled={step === STEP_LEVEL_GOAL || submitting}
                                    className="text-stone-600"
                                >
                                    <ArrowLeft className="h-4 w-4 mr-2" />
                                    Back
                                </Button>
                                {step < STEP_TARGETS ? (
                                    <Button
                                        onClick={() => setStep(nextStep)}
                                        disabled={!canAdvance || submitting}
                                    >
                                        Continue
                                        <ArrowRight className="h-4 w-4 ml-2" />
                                    </Button>
                                ) : (
                                    <Button onClick={handleSubmit} disabled={!canAdvance || submitting}>
                                        {submitting ? (
                                            <>
                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                Setting things up…
                                            </>
                                        ) : (
                                            <>
                                                Finish
                                                <ArrowRight className="h-4 w-4 ml-2" />
                                            </>
                                        )}
                                    </Button>
                                )}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </main>
        </div>
    );
};

export default Onboarding;
